// @flow

import _ from 'lodash';

import ArrayUtils from '../../utils/ArrayUtils';

import ApiObject from '../ApiObject';
import CategoryItem from './CategoryItem';
import ProductPrices from './ProductPrices';
import Order from '../order/Order';
import CartModifier from './CartModifier';

import AvailabilityDates from './AvailabilityDates';
import AvailabilityHours from './AvailabilityHours';

import { getLocalizedData } from '../../utils/CatalogUtils';

export default class Product extends ApiObject {
  static DEFAULT_STOCK = 999;

  type = CategoryItem.Type.PRODUCT;

  id: string = '';

  name: string = '';

  localizedNames: Array<> = [];

  description: string = '';

  localizedDescriptions: Array<> = [];

  image: string = ''; // Isn't used for the moment.

  prices: ProductPrices = new ProductPrices();

  /**
   * array of options' id.
   * @type {Array<String>}
   */
  optionsIds: Array<String> = [];

  defaultSequence: number = 0;

  stock: number = Product.DEFAULT_STOCK;

  availabilityDates: ?AvailabilityDates = null;

  availabilityHours: ?AvailabilityHours = null;

  cartModifiers: Array = [];

  // a front only var set by RestaurantMenu when filtered to indicate that the menu should be
  // display as unavailable
  unavailable: boolean = false;

  allergen = [];

  reclaimLaterEnabled = false;

  set name(name) {
    this._name = name;
  }

  get name() {
    return getLocalizedData(this.localizedNames) || this._name || '';
  }

  set description(description) {
    this._description = description;
  }

  get description() {
    return (
      getLocalizedData(this.localizedDescriptions) || this._description || ''
    );
  }

  postJsonAssignation(data: Object) {
    this.prices = new ProductPrices();
    this.prices.fromJson(data.prices);

    if (!_.isNil(data.availabilityHours)) {
      this.availabilityHours = new AvailabilityHours().fromJson(
        data.availabilityHours
      );
    }

    if (!_.isNil(data.availabilityDates)) {
      this.availabilityDates = new AvailabilityDates().fromJson(
        data.availabilityDates
      );
    }
  }

  //
  // utils
  //

  isActivatedForMode(mode: OrderMode) {
    return this.prices.getMode(mode).available;
  }

  toggleActivationForMode(mode: OrderMode) {
    this.prices.getMode(mode).available = !this.prices.getMode(mode).available;
  }

  getPriceForMode(mode: OrderMode): number {
    return this.prices.getMode(mode).priceIncludingTax;
  }

  getCartModifiersPrice(mode: OrderMode): number {
    let price = this.getPriceForMode(mode);

    if (this.cartModifiers.length > 0) {
      let priceTemp = 0;

      this.cartModifiers.map(cartModifier => {
        if (priceTemp === 0) {
          priceTemp = price;
        }

        if (cartModifier.isHandled && cartModifier.isAvailable) {
          if (
            cartModifier.type === CartModifier.ActionType.DISCOUNT_PERCENTAGE
          ) {
            priceTemp = parseFloat(
              ((priceTemp * (100 - cartModifier.value)) / 100).toFixed(2)
            );
          } else {
            priceTemp = parseFloat((priceTemp - cartModifier.value).toFixed(2));
          }
        }
      });

      price = priceTemp;
    }

    return Math.max(0, price);
  }

  isActivatedForOneMode(): boolean {
    return (
      this.isActivatedForMode(Order.OrderMode.DELIVERY) ||
      this.isActivatedForMode(Order.OrderMode.TAKE_AWAY) ||
      this.isActivatedForMode(Order.OrderMode.ON_SITE)
    );
  }

  toggleAvailibility() {
    if (this.stock === 0) {
      this.stock = Product.DEFAULT_STOCK;
    } else {
      this.stock = 0;
    }
  }

  isAvailableForMode(mode: number): boolean {
    return this.isActivatedForMode(mode) && this.haveStock();
  }

  haveOptions(): boolean {
    return !_.isEmpty(this.optionsIds);
  }

  haveOptionWithId(id: string): boolean {
    return ArrayUtils.exists(this.optionsIds, (optionId: string) => {
      return id === optionId;
    });
  }

  /**
   *
   */
  haveStock(): boolean {
    return this.stock !== 0;
  }

  haveStockAndIsActivated(): boolean {
    return this.haveStock() && this.isActivatedForOneMode();
  }

  isActivatedForDate(date: Moment): boolean {
    return (
      _.isNil(this.availabilityDates) ||
      this.availabilityDates.isAvailableForDate(date)
    );
  }

  isAvailableForDate(date: Moment): boolean {
    return (
      _.isNil(this.availabilityHours) ||
      this.availabilityHours.isAvailableForDate(date)
    );
  }

  //
  // Validation
  //

  /**
   * Verify that the object is correct
   */
  isValid(): boolean {
    // TODO: update
    return (
      this.validateName() && this.validateDescription() && this.validatePrices()
    );
  }

  validateName(): boolean {
    if (_.isEmpty(this.localizedNames)) {
      return false;
    }

    let isValid = false;

    _.forEach(this.localizedNames, localizedName => {
      if (!_.isEmpty(localizedName.text)) {
        isValid = true;
      }
    });

    return isValid;
  }

  validateDescription(): boolean {
    let isValid = true;

    _.forEach(this.localizedDescriptions, localizedDescription => {
      if (
        !_.isEmpty(localizedDescription.text) &&
        localizedDescription.text.length < 5
      ) {
        isValid = false;
      }
    });

    return isValid;
  }

  validatePrices(): boolean {
    return this.prices.isValid();
  }

  static validationErrors = {
    name: 'Le nom ne peut être vide',
    description:
      'La description doit être vide ou contenir plus de 5 caractères',
    price: 'Un prix et/ou une taxe sont valides',
  };

  getValidationErrors(): Object {
    const validationErrors = Product.validationErrors;
    const errors = {};

    if (!this.validateName()) {
      errors.name = validationErrors.name;
    }

    if (!this.validateDescription()) {
      errors.description = validationErrors.description;
    }

    if (!this.validatePrices()) {
      errors.price = validationErrors.price;
    }

    return errors;
  }
}



// WEBPACK FOOTER //
// ./sdk/scripts/entities/menu/Product.js